// ** React
import React from 'react';

// ** MUI Components
import Stack from '@mui/material/Stack';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';

// ** Components
import TextSizeToggler from '@erikdakoda/site-template-ui/components/TextSizeToggler';
import UserAvatar, { type UserForAvatar } from '@erikdakoda/ui/components/UserAvatar';
import ClipboardButton from '@erikdakoda/ui/components/ClipboardButton';
import AdminOnly from '@erikdakoda/ui/components/AdminOnly';
import HoverListItem from '@erikdakoda/ui/components/HoverListItem';
import DisableAdminToggler from '@erikdakoda/site-template-ui/components/DisableAdminToggler';

// ** Hooks
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { useLayoutSettings } from '@erikdakoda/site-template-ui/hooks/useLayoutSettings';

// ** Utils
import { getUserName } from '@erikdakoda/auth/shared/utilities';
import themeConfig from '@erikdakoda/site-template-ui/config';
import {
  collapsedNavItemInsetPx,
  hiddenNavLabelSx,
  NAV_ITEM_AVATAR_WIDTH_PX,
  NAV_ITEM_ICON_LABEL_GAP,
} from '@erikdakoda/site-template-ui/layouts/components/vertical/navigation/navItemLayout';

type VerticalNavFooterProps = {
  navHover?: boolean;
};

const VerticalNavFooter = ({ navHover = false }: VerticalNavFooterProps) => {
  'use memo';

  const { settings } = useLayoutSettings();
  const { navCollapsed } = settings;
  const hideNavLabels = navCollapsed && !navHover;
  const collapsedAvatarInsetPx = collapsedNavItemInsetPx(
    themeConfig.config.collapsedNavigationSize,
    NAV_ITEM_AVATAR_WIDTH_PX,
  );

  // ** Hooks
  const { user } = useCurrentUser();

  if (!user) return null;

  return (
    <Stack
      sx={{
        width: '100%',
      }}
    >
      <Box
        key="togglers"
        className="hide-when-collapsed"
        sx={{
          px: '12px',
          py: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <TextSizeToggler fullWidth />
      </Box>
      <HoverListItem
        key="profile-header"
        padded
        secondaryAction={
          <AdminOnly>
            <ClipboardButton text={user?.id || ''} title={`Copy userId '${user?.id}'`} />
          </AdminOnly>
        }
        sx={{
          '&.MuiListItem-root': {
            alignItems: 'center',
            minHeight: 64,
            paddingLeft: `${collapsedAvatarInsetPx}px`,
          },
        }}
      >
        <ListItemAvatar
          sx={{
            minWidth: NAV_ITEM_AVATAR_WIDTH_PX,
            mr: NAV_ITEM_ICON_LABEL_GAP,
          }}
        >
          <UserAvatar user={user as unknown as UserForAvatar} />
        </ListItemAvatar>
        <ListItemText
          disableTypography
          secondary={
            <Box sx={{ marginTop: '6px' }}>
              <DisableAdminToggler />
            </Box>
          }
          slotProps={{
            root: {
              sx: {
                lineHeight: 1,
                minWidth: 0,
                my: 0,
              },
            },
          }}
        >
          <Box component="span" sx={hiddenNavLabelSx(hideNavLabels)}>
            {getUserName(user)}
          </Box>
        </ListItemText>
      </HoverListItem>
    </Stack>
  );
};

export default VerticalNavFooter;
